import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Shield, RefreshCw, CheckCircle2, XCircle, Trash2, Search, X, ArrowUpDown, ArrowUp, ArrowDown, Wrench, GitMerge, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import type { QAIssue } from '@/services/kbService';
import { QAErrorAlert } from './QAErrorAlert';
import { getSeverityBadgeClasses, getIssueTypeIcon, type PaginationState } from './qaUtils';

// ============================================================================
// Types
// ============================================================================

export interface QAIssuesTabProps {
  issues: QAIssue[];
  totalCount: number;
  loading: boolean;
  error: string | null;
  statusFilter: string;
  severityFilter: string;
  pagination: PaginationState;
  onStatusFilterChange: (value: string) => void;
  onSeverityFilterChange: (value: string) => void;
  onClearFilters: () => void;
  onPaginationChange: (pagination: PaginationState) => void;
  onRefresh: () => void;
  refreshing?: boolean;
  onResolve: (issue: QAIssue) => void;
  // New props for enhanced layout
  searchValue: string;
  onSearchChange: (value: string) => void;
  issueTypeFilter: string;
  onIssueTypeFilterChange: (value: string) => void;
  issueTypeBreakdown: [string, number][];
  selectedIds: Set<string>;
  onSelectIssue: (id: string, checked: boolean) => void;
  onSelectAll: (checked: boolean) => void;
  onBulkDismiss: () => void;
  onBulkDelete: () => void;
  onDismissSingle?: (issueId: string) => void;
  onDeleteSingle?: (issueId: string) => void;
  onAutoFixByType?: (issueType: string, action: string) => void;
  bulkActioning: boolean;
  // Optional: sort support
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSortChange?: (field: string) => void;
}

// ============================================================================
// Helpers
// ============================================================================

/** Map severity to a numeric confidence percentage for progress bar display */
const severityToConfidence = (severity: string): number => {
  switch (severity) {
    case 'critical': return 95;
    case 'high': return 75;
    case 'medium': return 50;
    case 'low': return 25;
    default: return 50;
  }
};

/** Issue types that represent entities that can be deleted (removed entirely) */
const DELETABLE_ISSUE_TYPES = new Set(['invalid_entry', 'invalid_format', 'noise', 'duplicate', 'orphan', 'misclassification', 'low_confidence', 'anomaly', 'invalid_relationship']);

// ============================================================================
// Issue Type Options (for the filter dropdown)
// ============================================================================

const ISSUE_TYPE_OPTIONS = [
  { value: 'misclassification', label: 'Misclassification' },
  { value: 'duplicate', label: 'Duplicate' },
  { value: 'anomaly', label: 'Anomaly' },
  { value: 'low_confidence', label: 'Low Confidence' },
  { value: 'noise', label: 'Noise' },
  { value: 'invalid_entry', label: 'Invalid Entry / Orphan' },
  { value: 'invalid_relationship', label: 'Invalid Relationship' },
];

// ============================================================================
// SortableHeader Component (same pattern as QACanonicalTab)
// ============================================================================

const SortableHeader: React.FC<{
  label: string;
  field: string;
  currentSort: string;
  currentOrder: 'asc' | 'desc';
  onSort: (field: string) => void;
  className?: string;
}> = ({ label, field, currentSort, currentOrder, onSort, className }) => (
  <TableHead className={className}>
    <button
      className="flex items-center gap-1 hover:text-foreground transition-colors"
      onClick={() => onSort(field)}
    >
      {label}
      {currentSort === field ? (
        currentOrder === 'asc' ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />
      ) : (
        <ArrowUpDown className="h-3 w-3 opacity-30" />
      )}
    </button>
  </TableHead>
);

// ============================================================================
// Main Component
// ============================================================================

export const QAIssuesTab: React.FC<QAIssuesTabProps> = ({
  issues,
  totalCount,
  loading,
  error,
  statusFilter,
  severityFilter,
  pagination,
  onStatusFilterChange,
  onSeverityFilterChange,
  onClearFilters,
  onPaginationChange,
  onRefresh,
  refreshing = false,
  onResolve,
  searchValue,
  onSearchChange,
  issueTypeFilter,
  onIssueTypeFilterChange,
  issueTypeBreakdown,
  selectedIds,
  onSelectIssue,
  onSelectAll,
  onBulkDismiss,
  onBulkDelete,
  onDismissSingle,
  onDeleteSingle,
  onAutoFixByType,
  bulkActioning,
  sortBy = '',
  sortOrder = 'asc',
  onSortChange,
}) => {
  const totalPages = Math.ceil(totalCount / pagination.pageSize);
  const isAllSelected = issues.length > 0 && selectedIds.size === issues.length;

  // Calculate the range of issues being shown
  const rangeStart = totalCount === 0 ? 0 : (pagination.current - 1) * pagination.pageSize + 1;
  const rangeEnd = Math.min(pagination.current * pagination.pageSize, totalCount);

  // View details state for resolved/ignored issues
  const [detailIssue, setDetailIssue] = useState<QAIssue | null>(null);

  // ---------------------------------------------------------------------------
  // Local search input state for debouncing (same pattern as QACanonicalTab)
  // ---------------------------------------------------------------------------
  const [localSearch, setLocalSearch] = useState(searchValue);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync local search with parent when searchValue changes externally
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Debounced search handler
  const handleSearchInput = useCallback((value: string) => {
    setLocalSearch(value);

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      onSearchChange(value);
    }, 300);
  }, [onSearchChange]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleClearSearch = useCallback(() => {
    setLocalSearch('');
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    onSearchChange('');
  }, [onSearchChange]);

  // Fallback sort handler for when onSortChange is not provided
  const handleSort = useCallback((field: string) => {
    if (onSortChange) {
      onSortChange(field);
    }
  }, [onSortChange]);

  return (
    <>
      {error && (
        <QAErrorAlert
          message="Failed to load issues"
          error={error}
          onRetry={onRefresh}
        />
      )}

      {/* ================================================================== */}
      {/* Row 1: Filters                                                     */}
      {/* ================================================================== */}
      <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-between gap-2 sm:gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {/* Search Input */}
          <div className="relative w-full sm:w-[240px]">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              placeholder="Search issues..."
              value={localSearch}
              onChange={(e) => handleSearchInput(e.target.value)}
              className="pl-8 sm:pl-9 pr-8 h-9 text-xs sm:text-sm"
            />
            {localSearch && (
              <button
                onClick={handleClearSearch}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {/* Issue Type Filter */}
          <Select value={issueTypeFilter} onValueChange={onIssueTypeFilterChange}>
            <SelectTrigger className="w-[130px] sm:w-[170px] text-xs sm:text-sm">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent className="dark">
              <SelectItem value="all">All Types</SelectItem>
              {ISSUE_TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Status Filter */}
          <Select value={statusFilter} onValueChange={onStatusFilterChange}>
            <SelectTrigger className="w-[100px] sm:w-[140px] text-xs sm:text-sm">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent className="dark">
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="resolved">Resolved</SelectItem>
              <SelectItem value="ignored">Ignored</SelectItem>
            </SelectContent>
          </Select>

          {/* Severity Filter */}
          <Select value={severityFilter} onValueChange={onSeverityFilterChange}>
            <SelectTrigger className="w-[110px] sm:w-[140px] text-xs sm:text-sm">
              <SelectValue placeholder="All Severities" />
            </SelectTrigger>
            <SelectContent className="dark">
              <SelectItem value="all">All Severities</SelectItem>
              <SelectItem value="critical">Critical</SelectItem>
              <SelectItem value="high">High</SelectItem>
              <SelectItem value="medium">Medium</SelectItem>
              <SelectItem value="low">Low</SelectItem>
            </SelectContent>
          </Select>

          {(searchValue || issueTypeFilter !== 'all' || statusFilter !== 'pending' || severityFilter !== 'all') && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
              onClick={() => {
                setLocalSearch('');
                onClearFilters();
              }}
            >
              <X className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
              <span className="hidden sm:inline">Clear Filters</span>
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            className="text-xs sm:text-sm"
            onClick={onRefresh}
            disabled={refreshing}
          >
            {refreshing ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2 animate-spin" /> : <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 sm:mr-2" />}
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>

        {/* ================================================================ */}
        {/* Row 1 (right): Bulk Actions                                      */}
        {/* ================================================================ */}
        <div className="flex flex-wrap items-center gap-2 sm:gap-4">
          {selectedIds.size > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="text-xs sm:text-sm"
              onClick={() => onSelectAll(false)}
            >
              {selectedIds.size} selected
              <X className="h-3.5 w-3.5 ml-1" />
            </Button>
          )}

          {/* Dismiss Selected Button - only show for non-ignored issues */}
          {statusFilter !== 'ignored' && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm"
                disabled={selectedIds.size === 0 || bulkActioning}
              >
                {bulkActioning ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" /> : <XCircle className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
                <span className="sm:hidden">{selectedIds.size}</span>
                <span className="hidden sm:inline">Dismiss Selected ({selectedIds.size})</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Dismiss Selected Issues</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to dismiss <strong>{selectedIds.size}</strong> selected issue{selectedIds.size !== 1 ? 's' : ''}?
                  Dismissed issues will be marked as ignored and hidden from their respective view.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onBulkDismiss}>
                  Dismiss All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          )}

          {/* Delete Selected Button */}
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button
                variant="destructive"
                size="sm"
                className="text-xs sm:text-sm"
                disabled={selectedIds.size === 0 || bulkActioning}
              >
                {bulkActioning ? <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 animate-spin" /> : <Trash2 className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />}
                <span className="sm:hidden">{selectedIds.size}</span>
                <span className="hidden sm:inline">Delete Selected ({selectedIds.size})</span>
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw] sm:max-w-lg">
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Selected Issues</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete <strong>{selectedIds.size}</strong> selected issues and their associated entities?
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={onBulkDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                  Delete All
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* ================================================================== */}
      {/* Issue Type Distribution Badges                                     */}
      {/* ================================================================== */}
      {issueTypeBreakdown.length > 0 && (
        <div className="mb-4 p-3 sm:p-4 bg-primary/10 border border-primary/30 rounded-lg">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs sm:text-sm font-medium text-primary">Issue Type Distribution</p>
            {onAutoFixByType && issueTypeFilter !== 'all' && (
              <div className="flex gap-2">
                {issueTypeFilter === 'noise' && (
                  <Button
                    variant="destructive"
                    size="sm"
                    className="text-xs h-7"
                    onClick={() => onAutoFixByType('noise', 'delete')}
                    disabled={bulkActioning}
                  >
                    <Trash2 className="h-3 w-3 mr-1" />
                    Delete All Noise
                  </Button>
                )}
                {issueTypeFilter === 'duplicate' && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-xs h-7 border-purple-500/40 text-purple-400 hover:bg-purple-500/10"
                    onClick={() => onAutoFixByType('duplicate', 'merge')}
                    disabled={bulkActioning}
                  >
                    <GitMerge className="h-3 w-3 mr-1" />
                    Auto-Merge All Duplicates
                  </Button>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-wrap gap-1 sm:gap-2">
            {issueTypeBreakdown.map(([type, count]) => (
              <Badge key={type} variant="outline" className="bg-primary/15 text-primary border-primary/30 text-[10px] sm:text-xs">
                {type.replace(/_/g, ' ').toUpperCase()}: {count}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* ================================================================== */}
      {/* Issues Content                                                     */}
      {/* ================================================================== */}
      {loading ? (
        <LoadingSpinner message="Loading issues..." fullScreen={false} />
      ) : issues.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-8 sm:py-12">
          <Shield className="h-8 w-8 sm:h-12 sm:w-12 text-muted-foreground mb-3 sm:mb-4" />
          {searchValue ? (
            <>
              <p className="text-sm sm:text-lg font-medium">No issues found</p>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                No results matching &quot;{searchValue}&quot;. Try a different search term.
              </p>
            </>
          ) : (
            <p className="text-sm sm:text-lg font-medium text-center">
              {statusFilter === 'pending' ? 'No pending issues - great job!' : 'No issues found'}
            </p>
          )}
        </div>
      ) : (
        <>
          {/* ============================================================== */}
          {/* Mobile: Card Layout                                            */}
          {/* ============================================================== */}
          <div className="sm:hidden space-y-3 max-h-[70vh] overflow-y-auto">
            {issues.map((issue) => (
              <Card key={issue.id} className="p-3">
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <Checkbox
                      checked={selectedIds.has(issue.id)}
                      onCheckedChange={(checked) => onSelectIssue(issue.id, checked as boolean)}
                    />
                    {getIssueTypeIcon(issue.issue_type)}
                    <span className="text-[10px] uppercase">{(issue.issue_type || 'unknown').replace(/_/g, ' ')}</span>
                    <Badge variant="outline" className={`text-[10px] ${getSeverityBadgeClasses(issue.severity)}`}>
                      {(issue.severity || 'unknown').toUpperCase()}
                    </Badge>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30 text-[10px]">
                      {issue.entity_type || 'Unknown'}
                    </Badge>
                    <span className="font-medium text-xs truncate max-w-[180px]">{issue.entity_value || 'N/A'}</span>
                  </div>
                  <p className="text-[11px] text-muted-foreground line-clamp-2">{issue.description || 'No description'}</p>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>Severity: {Math.round(severityToConfidence(issue.severity))}%</span>
                  </div>
                </div>
                {/* Quick action buttons for mobile */}
                <div className="flex items-center gap-1 mt-2 pt-2 border-t">
                  {issue.status === 'pending' && (
                    <>
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                              onClick={() => onResolve(issue)}
                            >
                              <CheckCircle2 className="h-3.5 w-3.5" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent className="dark">Resolve issue</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      <AlertDialog>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <AlertDialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7 text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                                >
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              </AlertDialogTrigger>
                            </TooltipTrigger>
                            <TooltipContent className="dark">Dismiss issue</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                        <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw]">
                          <AlertDialogHeader>
                            <AlertDialogTitle>Dismiss Issue</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to dismiss this issue for <strong>{issue.entity_value}</strong>? It will be marked as ignored.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => onDismissSingle?.(issue.id)}>
                              Dismiss
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>

                      {DELETABLE_ISSUE_TYPES.has(issue.issue_type) && (
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-7 w-7 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent className="dark bg-background text-foreground max-w-[90vw]">
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Entity</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete the entity <strong>{issue.entity_value}</strong>? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction onClick={() => onDeleteSingle?.(issue.id)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      )}
                    </>
                  )}
                  {issue.status !== 'pending' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-6 gap-1 px-1"
                      onClick={() => setDetailIssue(issue)}
                    >
                      <Badge variant={issue.status === 'resolved' ? 'default' : 'secondary'} className="text-[10px]">
                        {(issue.status || 'unknown').toUpperCase()}
                      </Badge>
                      <Eye className="h-3 w-3 text-muted-foreground" />
                    </Button>
                  )}
                </div>
              </Card>
            ))}
          </div>

          {/* ============================================================== */}
          {/* Desktop: Table Layout                                          */}
          {/* ============================================================== */}
          <div className="hidden sm:block border rounded-lg">
            <div className="max-h-[70vh] overflow-y-auto overflow-x-auto">
              <Table>
                <TableHeader className="sticky top-0 bg-background z-10 shadow-[0_1px_0_0_hsl(var(--border))]">
                  <TableRow>
                    <TableHead className="w-12">
                      <Checkbox
                        checked={isAllSelected}
                        onCheckedChange={onSelectAll}
                      />
                    </TableHead>
                    {onSortChange ? (
                      <>
                        <SortableHeader label="Issue Type" field="issue_type" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} className="w-[140px]" />
                        <SortableHeader label="Entity" field="entity_value" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                        <TableHead>Description</TableHead>
                        <SortableHeader label="Severity" field="severity" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} className="w-[130px]" />
                      </>
                    ) : (
                      <>
                        <TableHead className="w-[140px]">Issue Type</TableHead>
                        <TableHead>Entity</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="w-[130px]">Severity</TableHead>
                      </>
                    )}
                    <TableHead className="w-[120px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {issues.map((issue) => (
                    <TableRow key={issue.id} className="hover:bg-muted/50">
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(issue.id)}
                          onCheckedChange={(checked) => onSelectIssue(issue.id, checked as boolean)}
                        />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getIssueTypeIcon(issue.issue_type)}
                          <span className="text-sm">{(issue.issue_type || 'unknown').replace(/_/g, ' ').toUpperCase()}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <div className="flex items-center gap-2">
                                <Badge variant="outline" className="bg-primary/10 text-primary border-primary/30">
                                  {issue.entity_type || 'Unknown'}
                                </Badge>
                                <span className="font-medium truncate max-w-[150px]">{issue.entity_value || 'N/A'}</span>
                              </div>
                            </TooltipTrigger>
                            <TooltipContent className="dark">
                              <div className="space-y-1">
                                <p>Type: {issue.entity_type || 'Unknown'}</p>
                                <p>Document: {issue.document_name || 'Unknown'}</p>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="truncate max-w-[200px] block">{issue.description || 'No description'}</span>
                            </TooltipTrigger>
                            <TooltipContent className="dark max-w-sm">{issue.description}</TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Progress
                            value={severityToConfidence(issue.severity)}
                            className="w-16 h-2"
                          />
                          <Badge variant="outline" className={`text-xs ${getSeverityBadgeClasses(issue.severity)}`}>
                            {(issue.severity || 'unknown').toUpperCase()}
                          </Badge>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          {/* Resolve (green checkmark) */}
                          {issue.status === 'pending' && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                    onClick={() => onResolve(issue)}
                                  >
                                    <CheckCircle2 className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="dark">Resolve issue</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}

                          {/* Dismiss (orange X) */}
                          {issue.status === 'pending' && onDismissSingle && (
                            <AlertDialog>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-amber-500 hover:text-amber-400 hover:bg-amber-500/10"
                                      >
                                        <XCircle className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark">Dismiss issue</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <AlertDialogContent className="dark bg-background text-foreground">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Dismiss Issue</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to dismiss this issue for <strong>{issue.entity_value}</strong>? It will be marked as ignored.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => onDismissSingle(issue.id)}>
                                    Dismiss
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}

                          {/* Delete entity (red trash) - only for noise/invalid types */}
                          {issue.status === 'pending' && DELETABLE_ISSUE_TYPES.has(issue.issue_type) && (
                            <AlertDialog>
                              <TooltipProvider>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <AlertDialogTrigger asChild>
                                      <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </AlertDialogTrigger>
                                  </TooltipTrigger>
                                  <TooltipContent className="dark">Delete entity</TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                              <AlertDialogContent className="dark bg-background text-foreground">
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete Entity</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to delete the entity <strong>{issue.entity_value}</strong>?
                                    This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => onDeleteSingle?.(issue.id)}
                                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                                  >
                                    Delete
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          )}

                          {/* Show status badge + view details for non-pending issues */}
                          {issue.status !== 'pending' && (
                            <TooltipProvider>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    className="h-7 gap-1 text-xs"
                                    onClick={() => setDetailIssue(issue)}
                                  >
                                    <Badge variant={issue.status === 'resolved' ? 'default' : 'secondary'} className="text-[10px]">
                                      {(issue.status || 'unknown').toUpperCase()}
                                    </Badge>
                                    <Eye className="h-3 w-3 text-muted-foreground" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent className="dark">View resolution details</TooltipContent>
                              </Tooltip>
                            </TooltipProvider>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>

          {/* ============================================================== */}
          {/* Pagination                                                     */}
          {/* ============================================================== */}
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mt-3 sm:mt-4 gap-2">
            <span className="text-xs sm:text-sm text-muted-foreground">
              {searchValue
                ? `Showing ${rangeStart}-${rangeEnd} of ${totalCount} results`
                : `Showing ${rangeStart}-${rangeEnd} of ${totalCount} issues`
              }
            </span>
            <div className="flex items-center gap-1 sm:gap-2">
              <Button
                variant="outline"
                size="sm"
                className="h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                onClick={() => onPaginationChange({ ...pagination, current: pagination.current - 1 })}
                disabled={pagination.current === 1}
              >
                Previous
              </Button>
              <span className="text-xs sm:text-sm px-1">
                Page {pagination.current} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                className="h-7 sm:h-8 px-2 sm:px-3 text-xs sm:text-sm"
                onClick={() => onPaginationChange({ ...pagination, current: pagination.current + 1 })}
                disabled={pagination.current >= totalPages}
              >
                Next
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Resolution Details Dialog */}
      <Dialog open={detailIssue !== null} onOpenChange={(open) => !open && setDetailIssue(null)}>
        <DialogContent className="dark bg-background text-foreground max-w-[95vw] sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5" />
              Resolution Details
            </DialogTitle>
          </DialogHeader>
          {detailIssue && (
            <div className="space-y-3">
              <div className="p-3 bg-muted/50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Entity</span>
                  <span className="font-medium flex items-center gap-1">
                    <Badge variant="outline" className="text-[10px]">{detailIssue.entity_type}</Badge>
                    {detailIssue.entity_value}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Issue Type</span>
                  <span className="font-medium">{(detailIssue.issue_type || '').replace(/_/g, ' ').toUpperCase()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Status</span>
                  <Badge variant={detailIssue.status === 'resolved' ? 'default' : 'secondary'} className="text-xs">
                    {(detailIssue.status || '').toUpperCase()}
                  </Badge>
                </div>
              </div>

              <div className="p-3 bg-muted/50 rounded-lg space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{detailIssue.status === 'ignored' ? 'Dismissal Action' : 'Resolution Action'}</span>
                  <span className="font-medium">{(detailIssue.resolution_action || '').replace(/_/g, ' ').toUpperCase() || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{detailIssue.status === 'ignored' ? 'Dismissed By' : 'Resolved By'}</span>
                  <span className="font-medium">{detailIssue.resolved_by || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">{detailIssue.status === 'ignored' ? 'Dismissed At' : 'Resolved At'}</span>
                  <span className="font-medium">{detailIssue.resolved_at || 'N/A'}</span>
                </div>
              </div>

              {(detailIssue.status === 'resolved' || detailIssue.status === 'ignored') && (
                <div className="p-3 bg-muted/50 rounded-lg text-sm">
                  <p className="text-muted-foreground mb-1">Notes</p>
                  <p>{detailIssue.resolution_notes || 'No notes added'}</p>
                </div>
              )}

              {detailIssue.description && (
                <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                  <p className="text-muted-foreground mb-1">Original Description</p>
                  <p className="text-amber-400">{detailIssue.description}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
